import { useEffect, useState } from "react";
import { Hammer, Package, Tag, Ruler, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { parseJobcard } from "../lib/jobcard";
import { jobcardImages } from "../lib/api";

function fmtPrecio(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n);
}

function FotoPlaceholder({ familia, jobcardId }) {
  const sigla = (familia ?? jobcardId ?? "??").slice(0, 3).toUpperCase();
  return (
    <div className="aspect-[4/3] w-full rounded-md bg-gradient-to-br from-blue-50 to-slate-100 border border-slate-200 flex items-center justify-center">
      <div className="flex flex-col items-center gap-1 text-slate-400">
        <Package className="w-8 h-8" />
        <div className="text-[11px] uppercase tracking-widest font-bold">{sigla}</div>
      </div>
    </div>
  );
}

function FotoCarrusel({ images, loading, familia, jobcardId }) {
  const [idx, setIdx] = useState(0);
  const [errored, setErrored] = useState(() => new Set());

  useEffect(() => {
    setIdx(0);
    setErrored(new Set());
  }, [images]);

  if (loading) {
    return (
      <div className="aspect-[4/3] w-full rounded-md bg-slate-50 border border-slate-200 flex items-center justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  const visibles = images.filter((_, i) => !errored.has(i));
  if (visibles.length === 0) {
    return <FotoPlaceholder familia={familia} jobcardId={jobcardId} />;
  }

  const total = visibles.length;
  const safeIdx = ((idx % total) + total) % total;
  const actual = visibles[safeIdx];
  const realIndex = images.indexOf(actual);

  return (
    <div className="space-y-1.5">
      <div className="relative aspect-[4/3] w-full rounded-md bg-white border border-slate-200 overflow-hidden">
        <img
          src={actual.url}
          alt={`${jobcardId ?? "imagen"} ${safeIdx + 1}`}
          className="w-full h-full object-contain"
          loading="lazy"
          onError={() =>
            setErrored((prev) => {
              const next = new Set(prev);
              next.add(realIndex);
              return next;
            })
          }
        />
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => setIdx((i) => i - 1)}
              className="absolute left-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-white/80 hover:bg-white text-slate-700 shadow"
              aria-label="Imagen anterior"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setIdx((i) => i + 1)}
              className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded-full bg-white/80 hover:bg-white text-slate-700 shadow"
              aria-label="Imagen siguiente"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
      {total > 1 && (
        <div className="text-[10px] text-slate-400 text-center tabular-nums">
          {safeIdx + 1} / {total}
        </div>
      )}
    </div>
  );
}

export default function JobCardDetailModal({ open, onClose, jobcard }) {
  const jobcardId = jobcard?.jobcard_id ?? jobcard?.id ?? null;
  const [images, setImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);

  useEffect(() => {
    if (!open || !jobcardId) {
      setImages([]);
      return;
    }
    let cancelado = false;
    setLoadingImages(true);
    setImages([]);
    jobcardImages(jobcardId)
      .then((res) => {
        if (cancelado) return;
        setImages(res?.images ?? []);
      })
      .catch((e) => {
        if (cancelado) return;
        console.warn("jobcard_images_error", e);
        setImages([]);
      })
      .finally(() => {
        if (!cancelado) setLoadingImages(false);
      });
    return () => {
      cancelado = true;
    };
  }, [open, jobcardId]);

  if (!jobcard) return null;
  const { titulo, descripcion, oficios, suministros, unidadDeMedida } = parseJobcard(
    jobcard.texto ?? "",
  );
  const precio = jobcard.precio_unitario ?? jobcard.coste_total ?? null;
  const unidad = jobcard.unidad ?? unidadDeMedida ?? "ud";
  const familia = jobcard.familia ?? null;
  const tituloFinal = titulo || jobcard.titulo || "Partida";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="jobcard-detail-modal">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogTitle className="text-base font-display font-bold text-slate-900 leading-tight">
            {tituloFinal}
          </DialogTitle>
          <div className="flex items-center gap-3 mt-1 text-[11px] uppercase tracking-widest font-bold text-slate-400">
            {familia && (
              <span className="inline-flex items-center gap-1">
                <Tag className="w-3 h-3" /> {familia}
              </span>
            )}
            {jobcardId && <span>· {jobcardId}</span>}
          </div>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4 max-h-[65vh] overflow-y-auto">
          <div className="grid grid-cols-[140px_1fr] gap-4 items-start">
            <FotoCarrusel
              images={images}
              loading={loadingImages}
              familia={familia}
              jobcardId={jobcardId}
            />
            <div className="space-y-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400">
                  Precio
                </div>
                <div className="text-lg font-display font-bold text-blue-700 tabular-nums leading-tight">
                  {fmtPrecio(precio)} €
                  <span className="text-xs font-normal text-slate-500"> /{unidad}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Ruler className="w-3 h-3" /> Unidad: {unidad}
              </div>
            </div>
          </div>

          {descripcion && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1">
                Descripción
              </div>
              <p className="text-xs text-slate-700 leading-relaxed">{descripcion}</p>
            </div>
          )}

          {oficios.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1 flex items-center gap-1">
                <Hammer className="w-3 h-3" /> Oficios
              </div>
              <ul className="text-xs text-slate-700 space-y-0.5 list-disc list-inside">
                {oficios.map((o, i) => (
                  <li key={i}>{o}</li>
                ))}
              </ul>
            </div>
          )}

          {suministros.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-400 mb-1 flex items-center gap-1">
                <Package className="w-3 h-3" /> Suministros
              </div>
              <ul className="text-xs text-slate-700 space-y-1 list-disc list-inside">
                {suministros.slice(0, 5).map((s, i) => (
                  <li key={i} className="leading-snug">{s}</li>
                ))}
                {suministros.length > 5 && (
                  <li className="list-none text-slate-400 italic">
                    + {suministros.length - 5} más…
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
